import { useRef } from 'react';

import { Box, Col, Image, PressableBox, Row, Text } from '@opendoor/bricks-next';
import { IconChevronRight } from '@tabler/icons-react';
import { EntryComponent } from 'cms/entries/entries';
import { IFcTestimonialCarousel, IFcTestimonialCarouselCard } from 'declarations/contentful';

import { resizeTestimonialCardImageUrl } from './shared/utils';

const inheritStyle = {
  fontSize: 'inherit !important',
  fontWeight: 'inherit !important',
  lineHeight: 'inherit !important',
  letterSpacing: 'inherit !important',
};

const renderHighlightedTitle = (title: string, highlightedText: string | undefined) => {
  if (!highlightedText || !title?.includes(highlightedText)) return title;

  return title.split(highlightedText).map((part: string, i: number) =>
    i === 0 ? (
      part
    ) : (
      <Text key={i} tag="span" typography="$headerSmall" color="$contentPrimary" css={inheritStyle}>
        <Text tag="span" typography="$headerSmall" color="$contentAccentBrand" css={inheritStyle}>
          {highlightedText}
        </Text>
        {part}
      </Text>
    ),
  );
};

const TestimonialCard = ({ entry }: { entry: IFcTestimonialCarouselCard }) => {
  const { fields } = entry;
  const imageUrl = fields.image?.fields?.file?.url;
  const resizedImageUrl = imageUrl ? resizeTestimonialCardImageUrl(imageUrl) : null;

  return (
    <Box
      width={350}
      minWidth={350}
      minHeight={495}
      borderRadius="$10x"
      overflow="hidden"
      backgroundColor="$backgroundPrimary"
      flex={1}
      padding="$4x"
      pb="$16x"
    >
      {resizedImageUrl && (
        <Box paddingHorizontal="$4x" paddingTop="$4x" paddingBottom={12} overflow="hidden">
          <Image
            source={{ uri: resizedImageUrl }}
            alt={fields.name}
            width="100%"
            height={200}
            style={{ borderRadius: 10 }}
            $platform-web={{ objectFit: 'cover', loading: 'lazy' }}
          />
        </Box>
      )}
      <Col padding="$6x" flex={1} justifyContent="space-between">
        <Col>
          <Text
            tag="p"
            typography="$labelSmall"
            style={{ fontWeight: 600, fontSize: 16 }}
            color="$contentPrimary"
            textAlign="center"
          >
            {fields.name}
          </Text>
          <Text
            tag="p"
            typography="$bodySmall"
            textAlign="center"
            style={{
              fontSize: 15,
              fontWeight: 400,
              lineHeight: '110%',
              letterSpacing: '-0.15px',
              color: 'rgba(63, 56, 50, 0.75)',
            }}
          >
            {fields.locationContext}
          </Text>
        </Col>
        <Text
          tag="p"
          typography="$bodyLarge"
          color="$contentPrimary"
          marginTop="$4x"
          marginHorizontal={12}
          flex={1}
          textAlign="center"
          style={{
            fontSize: 18,
            fontWeight: 400,
            lineHeight: '150%',
            letterSpacing: '-0.18px',
          }}
        >
          ”{fields.quote}”
        </Text>
        {fields.readMoreUrl && (
          <Box alignItems="center" marginTop="$4x">
            <a href={fields.readMoreUrl} style={{ textDecoration: 'none' }}>
              <Text
                tag="span"
                typography="$bodySmall"
                color="$contentAccentBrand"
                textTransform="uppercase"
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 'normal',
                  letterSpacing: '1.2px',
                }}
              >
                read more
              </Text>
            </a>
          </Box>
        )}
      </Col>
    </Box>
  );
};

const ArrowButton = ({
  direction,
  onClick,
}: {
  direction: 'left' | 'right';
  onClick: () => void;
}) => {
  return (
    <PressableBox
      p="$5x"
      display="flex"
      alignItems="center"
      justifyContent="center"
      style={{
        backgroundColor: '#0040e6',
      }}
      borderRadius={50}
      aria-label={direction === 'left' ? 'Previous testimonial' : 'Next testimonial'}
      onPress={onClick}
      analyticsName="testimonials"
    >
      <IconChevronRight
        size={40}
        color="white"
        style={{ transform: direction === 'left' ? 'rotate(180deg)' : undefined }}
      />
    </PressableBox>
  );
};

const RenderFCTestimonialCarousel = (entry: IFcTestimonialCarousel) => {
  const { fields } = entry;
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = direction === 'left' ? -370 : 370;
    scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  return (
    <Col width="100%" style={{ backgroundColor: '#ece9e7' }} pt="$40x" pb="$50x" gap="$40x">
      <Row justifyContent="center">
        <Text
          tag="h2"
          typography="$displayXsmall"
          textAlign="center"
          color="$contentPrimary"
          maxWidth={923}
          $smallerThanLG={{ typography: '$headerLarge', px: '$30x' }}
        >
          {renderHighlightedTitle(fields.title, fields.highlightedText)}
        </Text>
      </Row>
      <Col gap="$30x">
        {/* Carousel */}
        <Row
          ref={scrollRef}
          gap={20}
          css={{
            '&::-webkit-scrollbar': { display: 'none' },
            paddingLeft: '40px !important',
            paddingRight: '40px !important',
            scrollPaddingLeft: 40,
            '@media (min-width: 640px)': {
              paddingLeft: '80px !important',
              paddingRight: '80px !important',
              scrollPaddingLeft: 80,
            },
            '@media (min-width: 1024px)': {
              paddingLeft: '128px !important',
              paddingRight: '128px !important',
              scrollPaddingLeft: 128,
            },
          }}
          style={{
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
          }}
        >
          {fields.cards?.map((card: IFcTestimonialCarouselCard) => (
            <Box key={card.sys.id} style={{ scrollSnapAlign: 'start' }}>
              <TestimonialCard entry={card} />
            </Box>
          ))}
        </Row>
        {/* Navigation arrows */}
        <Row
          maxWidth={1440}
          width="100%"
          marginHorizontal="auto"
          justifyContent="center"
          gap="$10x"
        >
          <ArrowButton direction="left" onClick={() => scroll('left')} />
          <ArrowButton direction="right" onClick={() => scroll('right')} />
        </Row>
      </Col>
    </Col>
  );
};

const FCTestimonialCarousel: EntryComponent<IFcTestimonialCarousel> = {
  render: RenderFCTestimonialCarousel,
};

export default FCTestimonialCarousel;
